import { registerComponent } from "helpers";
import WYATTElement from "wyatt";

/*
 * 1. [removed]
 * 2. Reset bounds before new search results come in.
 */

WYATT.storeLocator = {
  googleMap: null,
  googleMapBounds: null,
  storeTypes: ["traditional"],
};

registerComponent(
  "store-locator",
  class StoreLocator extends WYATTElement {
    constructor() {
      super();

      this.searchForm = this.querySelector("[data-search-form]");
      this.searchInput = this.querySelector("[data-search-input]");
      this.searchButton = this.searchForm?.querySelector(
        'button[type="submit"]',
      );
      this.clearButton = this.querySelector("[data-clear-button]");
      this.map = this.querySelector("[data-map]");
      this.filters = this.querySelectorAll("[name='store_type']");
      this.isSearching = false;

      this.onSetup = this.setup.bind(this);
      this.onUserLocationChange = this.fitBounds.bind(this);
      this.onSearchSubmit = this.handleSearchSubmit.bind(this);
      this.onToggleClearButton = this.toggleClearButton.bind(this);
      this.onClearInput = this.handleClearInput.bind(this);

      this.filters.forEach((filter) => {
        filter.addEventListener("change", this.handleFilterChange.bind(this));
      });
    }

    connectedCallback() {
      this.subscribe(WYATT.PubSub.events.storesFetched, this.onSetup);
      this.subscribe(
        WYATT.PubSub.events.userLocationChange,
        this.onUserLocationChange,
      );
    }

    get formData() {
      return new FormData(this.searchForm);
    }

    get radius() {
      return parseInt(this.formData.get("distance"), 10);
    }

    get location() {
      return this.formData.get("location");
    }

    get storeTypes() {
      return this.formData.getAll("store_type");
    }

    async setup() {
      if (this.map) {
        await this.loadDependencies();
        await this.initMap();
      }

      this.bindEvents();
    }

    async loadDependencies() {
      const { Map, Circle } = await google.maps.importLibrary("maps");
      this.Map = Map;
      this.Circle = Circle;

      this.bounds = new google.maps.LatLngBounds();
    }

    async initMap() {
      const center =
        WYATT.Geolocation.location.coordinates || this.config.defaultLocation;

      this.map = new this.Map(this.map, {
        zoom: 7,
        center,
        mapId: this.config.id,
      });

      WYATT.storeLocator.googleMap = this.map;
      WYATT.storeLocator.googleMapBounds = this.bounds;

      this.publish(WYATT.PubSub.events.googleMapReady);

      // Apply any location that arrived (or was replayed) before the map was ready.
      this.fitBounds();
    }

    bindEvents() {
      if (this.eventsBound) {
        return;
      }

      this.eventsBound = true;

      this.searchForm.addEventListener("submit", this.onSearchSubmit);

      if (this.searchInput && this.clearButton) {
        this.searchInput.addEventListener("input", this.onToggleClearButton);
        this.clearButton.addEventListener("click", this.onClearInput);
        this.toggleClearButton();
      }
    }

    toggleClearButton() {
      this.clearButton.hidden = !this.searchInput.value;
    }

    handleClearInput() {
      this.searchInput.value = "";
      this.searchInput.focus();
      this.toggleClearButton();
    }

    fitBounds() {
      // `this.map` starts as the DOM container; only fit after Map + Circle are loaded.
      // userLocationChange can replay while setup() is still awaiting importLibrary.
      if (!WYATT.storeLocator.googleMap || !this.Circle) {
        return;
      }

      const center =
        WYATT.Geolocation.location.coordinates || this.config.defaultLocation;

      const circle = new this.Circle({
        center,
        radius: this.radius,
      });

      this.map.fitBounds(circle.getBounds());
    }

    handleFilterChange(event) {
      event.preventDefault();

      WYATT.storeLocator.storeTypes = this.storeTypes;

      this.bounds = new google.maps.LatLngBounds(); /* [2] */
      WYATT.storeLocator.googleMapBounds = this.bounds;

      this.publish(WYATT.PubSub.events.storesFiltered);
    }

    setSearchControlsDisabled(disabled) {
      // Prefer readOnly on the address input so FormData still includes its value
      // (disabled fields are omitted from FormData / form submission).
      if (this.searchInput) {
        this.searchInput.readOnly = disabled;
      }

      if (this.searchButton) {
        this.searchButton.disabled = disabled;
      }

      if (this.clearButton) {
        this.clearButton.disabled = disabled;
      }

      if (this.searchForm) {
        if (disabled) {
          this.searchForm.setAttribute("aria-busy", "true");
        } else {
          this.searchForm.removeAttribute("aria-busy");
        }
      }
    }

    async handleSearchSubmit(event) {
      event.preventDefault();

      if (this.isSearching) {
        return;
      }

      WYATT.storeLocator.storeTypes = this.storeTypes;

      if (!this.location) {
        return;
      }

      this.isSearching = true;
      this.setSearchControlsDisabled(true);

      if (this.clearButton) {
        this.clearButton.hidden = true;
      }

      try {
        this.bounds = new google.maps.LatLngBounds(); /* [2] */
        WYATT.storeLocator.googleMapBounds = this.bounds;

        await WYATT.Geolocation.updateLocationFromSearchForm(this.searchForm);
      } finally {
        this.isSearching = false;
        this.setSearchControlsDisabled(false);

        if (this.clearButton) {
          this.toggleClearButton();
        }
      }
    }
  },
);
